import { useEditor, useNode } from "@craftjs/core";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  getAllRecordings,
  getOrganizationExternalId,
  getPageSlug,
  getPageUrl,
} from "@/content-hub/store/selectors";
import { effectiveRecordingDisplay } from "@/content-hub/theme/primitives/video/recording-metadata-merge";
import { buildContentHubGridVideoShareUrl } from "@/content-hub/utils/build-content-hub-grid-video-share-url";
import { getCraftNodeDom } from "@/content-hub/utils/get-craft-node-dom";
import { inferYoutubePosterFromEmbedUrl } from "@/content-hub/utils/resolve-video-block-poster-for-details";
import { useAppSelector } from "@/redux/store";

import type { VideoGridProps } from "../video-grid";
import { SortableVideoItem } from "./sorted-video-item";
import { VideoGridEmbedMetadataPanel } from "./video-grid-embed-metadata-panel";
import { VideoGridRecordingMetadataPanel } from "./video-grid-recording-metadata-panel";

const getIdsFromVideoList = (videoList: any[]): string[] => {
  return videoList.map(({ id }) => {
    return id;
  });
};

export const VideoOrderSettings = () => {
  const { t } = useTranslation();
  const { query } = useEditor();
  const {
    id: videoGridCraftId,
    props,
    actions: { setProp },
  } = useNode((node) => {
    const filteredVideoList = node.props.videoList.filter((video) => {
      return typeof video !== "string";
    });
    return {
      id: node.id,
      props: {
        ...(node.props as VideoGridProps),
        videoList: filteredVideoList,
      },
    };
  });

  const recordings = useSelector(getAllRecordings);
  const [list, setList] = useState([]);
  const [editingListIndex, setEditingListIndex] = useState<number | null>(null);

  const organizationExternalId = useAppSelector(getOrganizationExternalId);
  const pageSlug = useAppSelector(getPageSlug);
  const pageUrl = useAppSelector(getPageUrl);

  const flags = useFlags();
  const directVideoSharingEnabled = Boolean(flags.contentHubDirectVideoSharing);

  const videos = useMemo(() => {
    return props.videoList.reduce(
      (videosAcc, currentVideo, listIndex) => {
        if (currentVideo.kind === "recording") {
          const { recordingId } = currentVideo;
          const recording = recordings.find(
            (recordingItem) => recordingItem.id === recordingId
          );
          if (recording) {
            const merged = effectiveRecordingDisplay(recording, currentVideo);
            videosAcc.push({
              ...recording,
              listIndex,
              kind: "recording" as const,
              title: merged.title,
              thumbnail: merged.thumbnail,
            });
          }
        } else {
          const customEmbedThumb = currentVideo.displayThumbnail?.trim();
          videosAcc.push({
            ...currentVideo,
            listIndex,
            kind: "embed" as const,
            title: (currentVideo.displayTitle ?? currentVideo.title) || "",
            thumbnail:
              customEmbedThumb ||
              currentVideo.thumbnail ||
              inferYoutubePosterFromEmbedUrl(currentVideo.url ?? "") ||
              undefined,
          });
        }
        return videosAcc;
      },
      [] as Array<
        | ({
            listIndex: number;
            kind: "recording";
          } & typeof recordings[number] & { title: string; thumbnail: string })
        | (Extract<
            NonNullable<VideoGridProps["videoList"]>[number],
            { kind: "embed" }
          > & { listIndex: number; kind: "embed" })
      >
    );
  }, [props.videoList, recordings]);

  useEffect(() => {
    setList(getIdsFromVideoList(videos));
  }, [videos]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    if (active.id === over.id) {
      return;
    }

    const activeIndex = list.findIndex((item) => item === active.id);
    const overIndex = list.findIndex((item) => item === over.id);
    const newList = arrayMove(list, activeIndex, overIndex);

    setList(newList);
    setProp((p) => {
      p.videoList = newList.map((videoId) => {
        return p.videoList.find((video) => {
          return video.id === videoId || video.recordingId === videoId;
        });
      });
    });
  };

  // Remove video from video order settings
  const removeVideo = (videoIndex: number) => {
    const newList = list.filter((__, index) => index !== videoIndex);

    setList(newList);
    setProp((p) => {
      p.videoList = newList.map((videoId) => {
        return p.videoList.find((video) => {
          return video.id === videoId || video.recordingId === videoId;
        });
      });
    });
  };

  if (!list && list?.length === 0) {
    return null;
  }

  const editingListItem =
    editingListIndex !== null ? props.videoList[editingListIndex] : undefined;
  const editingRecording =
    editingListItem &&
    typeof editingListItem !== "string" &&
    editingListItem.kind === "recording"
      ? recordings.find((r) => r.id === editingListItem.recordingId)
      : undefined;

  return (
    <div className="mt-2">
      <h2 className="mb-2 text-sm font-medium">
        {t("preference-panel.video-order")}
      </h2>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={list} strategy={verticalListSortingStrategy}>
          {videos.map((video, index) => (
            <SortableVideoItem
              key={video.id}
              video={video}
              analytics={
                video.kind === "recording"
                  ? { kind: "recording", recordingId: video.id }
                  : { kind: "embed", id: video.id }
              }
              deleteVideo={() => {
                removeVideo(index);
              }}
              getShareUrl={
                directVideoSharingEnabled
                  ? () =>
                      buildContentHubGridVideoShareUrl({
                        organizationExternalId,
                        pageSlug,
                        pageUrl,
                        getGridRootElement: () =>
                          getCraftNodeDom(query, videoGridCraftId),
                        video: { id: video.id, title: video.title ?? null },
                      })
                  : undefined
              }
              onEditClick={
                directVideoSharingEnabled
                  ? () => setEditingListIndex(video.listIndex)
                  : undefined
              }
            />
          ))}
        </SortableContext>
      </DndContext>

      {editingListIndex !== null &&
        editingListItem &&
        typeof editingListItem !== "string" && (
          <>
            {editingListItem.kind === "recording" && (
              <VideoGridRecordingMetadataPanel
                open
                panelTitle={t("preference-panel.video-details-panel-title")}
                onClose={() => setEditingListIndex(null)}
                recording={editingRecording}
                listIndex={editingListIndex}
                listItem={editingListItem}
                videoGridCraftId={videoGridCraftId}
                setProp={setProp}
              />
            )}
            {editingListItem.kind === "embed" && (
              <VideoGridEmbedMetadataPanel
                open
                panelTitle={t("preference-panel.video-details-panel-title")}
                onClose={() => setEditingListIndex(null)}
                listIndex={editingListIndex}
                listItem={editingListItem}
                videoGridCraftId={videoGridCraftId}
                setProp={setProp}
              />
            )}
          </>
        )}
    </div>
  );
};
